import type { ReactNode } from "react";
import type { HeaderContext } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/utils/cn";
import type { DataTableSortHeaderOptions } from "./DataTable.types";

/**
 * Compact sort header (shadcn-style control — short bar, not full-width).
 * @see https://ui.shadcn.com/docs/components/base/data-table
 */
export function dataTableSortHeader<TData, TValue = unknown>(
  label: string,
  options?: DataTableSortHeaderOptions
): (ctx: HeaderContext<TData, TValue>) => ReactNode {
  const align = options?.align ?? "left";

  return function SortHeader({ column }) {
    if (!column.getCanSort()) {
      return (
        <span
          className={cn(
            "block break-words text-left font-heading text-xs font-bold uppercase leading-snug tracking-wider text-dark-invirtus",
            align === "right" && "text-right"
          )}
        >
          {label}
        </span>
      );
    }
    const sorted = column.getIsSorted();
    const iconMuted = sorted ? "text-dark-invirtus/70" : "text-dark-invirtus/45";
    const icon =
      sorted === "asc" ? (
        <ArrowUp className={cn("h-3.5 w-3.5 shrink-0", iconMuted)} strokeWidth={2} />
      ) : sorted === "desc" ? (
        <ArrowDown className={cn("h-3.5 w-3.5 shrink-0", iconMuted)} strokeWidth={2} />
      ) : (
        <ChevronsUpDown className={cn("h-3.5 w-3.5 shrink-0", iconMuted)} strokeWidth={2} />
      );

    return (
      <div className={cn("flex min-w-0", align === "right" ? "justify-end" : "justify-start")}>
        <button
          type="button"
          className={cn(
            "-ml-1 flex max-w-none items-start gap-2 rounded-md px-1.5 py-1 text-left font-heading text-xs font-bold whitespace-nowrap uppercase leading-snug tracking-wider text-dark-invirtus hover:text-grey cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/30",
            align === "right" && "ml-auto items-end text-right"
          )}
          onClick={() => column.toggleSorting(sorted === "asc")}
          aria-label={`Sort by ${label}${sorted === "asc" ? ", ascending" : sorted === "desc" ? ", descending" : ""}`}
        >
          <span className="min-w-0 truncate">{label}</span>
          {icon}
        </button>
      </div>
    );
  };
}
